import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "@emotion/styled";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../utils/cms.utils";
import { getUserSubscriptionStatus } from "../../utils/data.utils";
import { FOCUS_CONFIG, FOCUS_BORDER } from "../../config/focusStyles";
import OptimizedImage from "../common/OptimizedImage";
import { useInputMode } from "../../utils/hooks/useInputMode";
import httpService from "../../services/http.service";
import { getCMSApiRoute } from "../../utils/api.utils";

const ContentRowScrollingWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-flex-shrink: 1;
  flex-shrink: 1;
  -webkit-flex-grow: 1;
  flex-grow: 1;
  padding-left: 60px;
`;

const ContentRowScrollingContent = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: row;
  flex-direction: row;
  -webkit-transition: transform 0.5s;
  transition: transform 0.5s;

  &:nth-child(1) {
    margin-right: 5%;
  }
`;

const ContentRowWrapper = styled.div`
  margin-bottom: 4% !important;
  width: 100%;
  margin: 0;
  position: relative;

  &:after {
    content: "";
    position: absolute;
    width: 150px;
    height: 100%;
    background: -webkit-linear-gradient(left, #121212, rgba(0, 0, 0, 0));
    background: linear-gradient(to right, #121212, rgba(0, 0, 0, 0));
    left: 0;
    top: 0;
    pointer-events: none;
  }

  &:before {
    content: "";
    position: absolute;
    width: 150px;
    height: 100%;
    background: -webkit-linear-gradient(right, #121212, rgba(0, 0, 0, 0));
    background: linear-gradient(to left, #121212, rgba(0, 0, 0, 0));
    right: 0;
    top: 0;
    z-index: 1;
    pointer-events: none;
  }
`;

const ContentRowTitle = styled.h3`
  color: white;
  margin-bottom: 22px;
  font-size: 27px;
  font-weight: 700;
  padding-left: 60px;
  margin-top: 0;
  font-family: 'Anjoman Max FN', sans-serif;
`;

const EpisodeItemWrapper = styled.div`
  width: 380px;
  margin-left: 2rem;
  padding-bottom: 2rem;
  position: relative;
`;

const EpisodeCardContainer = styled.div`
  width: 380px;
  height: 250px;
  border-radius: 12px;
  -webkit-border-radius: 12px;
  background-size: 100% 100%;
  background-position: center;
  margin-bottom: 5%;
  overflow: hidden;
  z-index: ${(props) => (props.focused ? 10 : 0)};
  margin: 2px;
  margin-left: 1rem;
  position: relative;
  border: ${(props) => (props.focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive)};
  cursor: pointer;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  -webkit-transition: all 0.15s ease;
  transition: all 0.15s ease;
`;

const EpisodeImage = styled.img`
  display: block;
  width: 100%;
  height: 250px;
  -o-object-fit: cover;
  object-fit: cover;
`;

const EpisodeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${(props) => (props.visible ? 1 : 0)};
  pointer-events: none;
  -webkit-transition: opacity 0.2s;
  transition: opacity 0.2s;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  padding-left: 16px;
  padding-right: 16px;
  -webkit-flex-direction: column;
  flex-direction: column;
`;

const OverlayLabel = styled.span`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  font-family: 'Anjoman Max FN', sans-serif;
`;

const EpisodeName = styled.p`
  color: white;
  font-size: 20px;
  font-weight: 700;
  text-align: center;
  margin-top: 8px;
  margin-bottom: 0;
  font-family: 'Anjoman Max FN', sans-serif;
`;

/**
 * Sentiment indicator icon for episodes - shows like/dislike status
 * Non-focusable, purely visual feedback
 * @param {string} variant - 'like' for green background, 'dislike' for red background
 */
const SentimentIndicator = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  width: 56px;
  height: 56px;
  background: ${(props) => props.variant === 'like' ? '#22c55e' : props.variant === 'dislike' ? '#ef4444' : 'rgba(0, 0, 0, 0.6)'};
  border-radius: 50%;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  pointer-events: none;
  z-index: 5;
`;

const EpisodeItem = ({
  item,
  onFocus,
  slug,
  overlayConfig,
  canPlayLoading,
  likeOrDislikeArray = [],
}) => {
  const { focused, ref, focusSelf } = useFocusable({
    onFocus,
    onEnterPress: () =>
      typeof overlayConfig.action === "function" && overlayConfig.action(),
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  const handleClick = () => {
    if (typeof overlayConfig.action === "function") {
      overlayConfig.action();
    }
  };

  // Get optimized image with LQIP support
  const episodeImageData = getOptimizedImageForType('episodeCover', item.data?.meta?.contentPhotos, 'TV', { width: 350, quality: 90, format: 'jpeg' });

  // Find matching like/dislike entry for this episode by season and episode number
  const episodeAction = likeOrDislikeArray.find(
    (action) => action.season === item.seasonNumber && action.episode === item.episodeNumber
  );
  const likeOrDislikeStatus = episodeAction?.likeOrDislike;

  return (
    <EpisodeItemWrapper ref={ref}>
      <EpisodeCardContainer focused={focused} onClick={handleClick} onMouseEnter={handleMouseEnter}>
        <OptimizedImage
          src={episodeImageData.src}
          alt={item.title}
          style={{
            display: 'block',
            width: '100%',
            height: '250px',
            objectFit: 'cover',
          }}
        />

        {/* Sentiment indicator - non-focusable visual feedback */}
        {likeOrDislikeStatus === 1 && (
          <SentimentIndicator variant="like">
            <img src="/svg/thumbs-up-filled.svg" alt="Liked" width={32} height={32} />
          </SentimentIndicator>
        )}
        {likeOrDislikeStatus === 2 && (
          <SentimentIndicator variant="dislike">
            <img src="/svg/thumbs-down-filled.svg" alt="Disliked" width={32} height={32} />
          </SentimentIndicator>
        )}
        <EpisodeOverlay visible={focused}>
          <OverlayLabel>
            {!canPlayLoading && overlayConfig?.label}
          </OverlayLabel>
        </EpisodeOverlay>
      </EpisodeCardContainer>
      <EpisodeName>{item.episodeName}</EpisodeName>
    </EpisodeItemWrapper>
  );
};

export const EpisodeSlider = ({
  movies = [],
  onFocus,
  rowTitle,
  movie,
  slug,
}) => {
  const { ref, focusKey } = useFocusable({ onFocus });
  const scrollingRef = useRef(null);
  const innerRef = useRef(null);
  const [canPlay, setCanPlay] = useState();
  const [canPlayLoading, setCanPlayLoading] = useState(true);
  const [likeOrDislikeArray, setLikeOrDislikeArray] = useState([]);
  const isLogin = localStorage.getItem("webTvUserToken");

  // Fetch content actions to get like/dislike status for each episode
  const fetchContentActions = async () => {
    if (!isLogin || !slug) return;
    try {
      const response = await httpService.get(getCMSApiRoute("action/content"), {
        params: { contentKey: slug },
      });
      if (response.status === 200) {
        const { data } = response.data;
        setLikeOrDislikeArray(data?.userActions?.likeOrDislike || []);
      }
    } catch (error) {
      console.log('[EpisodeSlider] Failed to fetch content actions:', error);
    }
  };

  const updateSubCondition = async () => {
    const subStatus = await getUserSubscriptionStatus();
    setCanPlay(subStatus > 0);
    setCanPlayLoading(false);
  };

  // Get isMouseFocus to guard horizontal scroll on hover
  const { isMouseFocus } = useInputMode();

  const onAssetFocus = useCallback(
    (props) => {
      // Skip horizontal scroll adjustment when focus originated from mouse hover
      if (isMouseFocus) return;
      var x = props.x;
      if (x < 0) {
        innerRef.current.style.transform =
          "translateX(" + Math.abs(x - 300) + "px)";
      } else {
        innerRef.current.style.transform = "translateX(0px)";
      }
    },
    [scrollingRef, isMouseFocus]
  );

  const navigate = useNavigate();

  const getActionOverlay = function (s, e) {
    if (!isLogin) {
      return {
        label: "ورود و تماشا",
        action: function () {
          navigate("/login?ref=" + encodeURI("/watch/" + slug + "/" + s + "/" + e));
        },
      };
    } else if (movie.isFree === 1) {
      return {
        label: "تماش کن",
        action: function () {
          navigate("/watch/" + slug + "/" + s + "/" + e);
        },
      };
    } else if (movie.isFree !== 1 && canPlay) {
      return {
        label: "تماشا کن",
        action: function () {
          navigate("/watch/" + slug + "/" + s + "/" + e);
        },
      };
    } else {
      return {
        label: "خرید اشتراک و تماشا",
        action: function () {
          navigate("/subscription");
        },
      };
    }
  };

  useEffect(function () {
    if (movies) {
      updateSubCondition();
      fetchContentActions();
    }
  }, []);

  if (!movies) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref}>
        <ContentRowTitle>{rowTitle}</ContentRowTitle>
        <ContentRowScrollingWrapper ref={scrollingRef}>
          <ContentRowScrollingContent ref={innerRef}>
            {movies?.map(function (item) {
              return (
                <EpisodeItem
                  item={item}
                  key={item.id}
                  onFocus={onAssetFocus}
                  slug={slug}
                  overlayConfig={getActionOverlay(
                    item.seasonNumber,
                    item.episodeNumber
                  )}
                  canPlayLoading={canPlayLoading}
                  likeOrDislikeArray={likeOrDislikeArray}
                />
              );
            })}
          </ContentRowScrollingContent>
        </ContentRowScrollingWrapper>
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
